import {
    AccountDataType,
    AccountSource,
    AccountSourceType,
    AdditionalAccountInfo,
    Characteristics,
    ExternalDataType,
    LoadState,
    M365AccountSource,
    M365ArchiveMailboxAccountSource,
    M365AuthProvider,
    M365ConnectedMailboxAccountSource,
    M365GroupMailboxAccountSource,
    M365Mailbox,
    M365MailboxType,
    m365MailboxTypeToMailboxType,
    M365PublicFolderMailboxAccountSource,
    M365SharedMailboxAccountSource,
    M365TeamsMailboxAccountSource,
    M365UserMailboxAccountSource,
} from 'owa-account-source-list-store';
import * as strings from 'owa-localize';
import type UnifiedGroup from 'owa-service/lib/contract/UnifiedGroup';
import { HostSourceInformation, SourceAuthType } from '../types';
import { buildSourceId, globalProfile, microsoft365SourceType } from './sourceIdBuilder';

export function buildOnlineArchiveMailboxSmtp(mailboxGuid: string, orgDomain: string): string {
    const mailboxHeaderFormat = '{0}@{1}';
    return strings.format(mailboxHeaderFormat, mailboxGuid, orgDomain);
}

export function buildMailboxCharacteristics(
    buildParams: Partial<Characteristics>
): Characteristics {
    return {
        // default values
        supportsCalendar: false,
        supportsContacts: false,
        supportsMail: false,
        supportsSettings: false,
        isMicrosoft365Hosted: true,

        ...buildParams,
    };
}

export function buildMailboxCharacteristicsFromHostSourceInformation(
    hostAccount: HostSourceInformation
): Characteristics {
    return buildMailboxCharacteristics({
        ...hostAccount.characteristics,
    });
}

export function buildAdditionalAccountInfo(
    hostingSourceId: string,
    loadState: LoadState = LoadState.NotStarted,
    sources: AccountSource[] = []
): AdditionalAccountInfo {
    return {
        hostingSourceId,
        loadState,
        sources,
    };
}

export function buildM365Mailbox(
    m365mailboxType: M365MailboxType,
    mailboxDisplayName: string,
    userIdentity: string,
    email: string,
    authProviderType: M365AuthProvider = M365AuthProvider.AAD
): M365Mailbox {
    return {
        type: m365MailboxTypeToMailboxType(m365mailboxType),
        name: mailboxDisplayName,
        displayName: mailboxDisplayName,
        emailAddress: email,
        logonEmailAddress: email,
        userIdentity,
        authProviderType,
    };
}

export function buildM365MailboxFromHostSourceInformation(
    m365mailboxType: M365MailboxType,
    hostAccount: HostSourceInformation
): M365Mailbox {
    return hostAccount.mailbox
        ? {
              ...hostAccount.mailbox,
              type: m365MailboxTypeToMailboxType(m365mailboxType),
              logonEmailAddress: hostAccount.mailbox.loginAddress,
              authProviderType:
                  hostAccount.authProviderType === SourceAuthType.Microsoft365
                      ? M365AuthProvider.AAD
                      : M365AuthProvider.MSA,
          }
        : buildM365Mailbox(m365mailboxType, '', '', '');
}

type RequiredOnPartial<T, RequiredKeys extends keyof T> = Required<Pick<T, RequiredKeys>> &
    Partial<Omit<T, RequiredKeys>>;

export function buildM365AccountSource(
    buildParams: RequiredOnPartial<
        M365AccountSource,
        'sourceId' | 'displayName' | 'characteristics' | 'm365mailbox' // required params
    >
): M365AccountSource {
    return {
        // optional buildParams props with default values
        sourceType: AccountSourceType.Office365,
        dataType: AccountDataType.M365Mailbox,
        externalData: { type: ExternalDataType.None },
        mailboxInfo: {
            type: buildParams.m365mailbox.type,
            userIdentity: buildParams.m365mailbox.userIdentity,
            mailboxSmtpAddress: buildParams.m365mailbox.emailAddress,
            sourceId: buildParams.sourceId,
        },
        // optional buildParams props with default values -- end here

        ...buildParams,
    };
}

function buildM365AccountSourceWithoutSourceId(
    m365mailboxType: M365MailboxType,
    displayName: string,
    smtpAddress: string,
    hostingUserIdentity: string,
    partialCharacteristics: Partial<Characteristics>
): M365AccountSource {
    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        smtpAddress,
        hostingUserIdentity
    );

    const characteristics = buildMailboxCharacteristics({
        ...partialCharacteristics,
    });

    const m365mailbox = buildM365Mailbox(
        m365mailboxType,
        smtpAddress,
        hostingUserIdentity,
        smtpAddress
    );
    return buildM365AccountSource({
        sourceId,
        displayName,
        characteristics,
        m365mailbox,
    });
}

export function buildM365UserAccountSource(
    sourceId: string,
    displayName: string,
    email: string,
    userIdentity: string,
    sourceType?: AccountSourceType
): M365UserMailboxAccountSource {
    const characteristics = buildMailboxCharacteristics({
        supportsMail: true,
        supportsCalendar: true,
        supportsContacts: true,
        supportsSettings: true,
    });

    const m365mailboxType = M365MailboxType.User;
    const m365mailbox = buildM365Mailbox(m365mailboxType, userIdentity, userIdentity, email);

    const m365AccountSource = buildM365AccountSource({
        sourceId,
        displayName,
        characteristics,
        m365mailbox,
        sourceType,
    });

    return {
        ...m365AccountSource,
        m365mailboxType,
        groupSources: buildAdditionalAccountInfo(sourceId),
        sharedSources: buildAdditionalAccountInfo(sourceId),
        archiveSources: buildAdditionalAccountInfo(sourceId),
        publicFolderSources: buildAdditionalAccountInfo(sourceId),
        teamsSources: buildAdditionalAccountInfo(sourceId),
    };
}

export function buildM365UserAccountSourceFromHostSourceInformation(
    sourceId: string,
    hostAccount: HostSourceInformation
): M365UserMailboxAccountSource {
    const characteristics = buildMailboxCharacteristicsFromHostSourceInformation(hostAccount);

    const m365mailboxType = M365MailboxType.User;
    const m365mailbox = buildM365MailboxFromHostSourceInformation(m365mailboxType, hostAccount);

    const sourceType =
        hostAccount.authProviderType === SourceAuthType.Microsoft365
            ? AccountSourceType.Office365
            : AccountSourceType.OutlookDotCom;

    const externalData = hostAccount.externalData;

    const m365AccountSource = buildM365AccountSource({
        sourceId,
        displayName: hostAccount.displayName,
        characteristics,
        m365mailbox,
        sourceType,
        externalData,
    });

    return {
        ...m365AccountSource,
        m365mailboxType,
        groupSources: buildAdditionalAccountInfo(sourceId),
        sharedSources: buildAdditionalAccountInfo(sourceId),
        archiveSources: buildAdditionalAccountInfo(sourceId),
        publicFolderSources: buildAdditionalAccountInfo(sourceId),
        teamsSources: buildAdditionalAccountInfo(sourceId),
    };
}

/**
 * create a new M365GroupMailboxAccountSource
 * @param memberOrOwnerUserIdentity userIdentity of the member of the owner of this group
 * @param group UnifiedGroup that has to be attached to the account source
 * @returns
 */
export function buildM365GroupMailboxAccountSource(
    memberOrOwnerUserIdentity: string,
    group: UnifiedGroup
): M365GroupMailboxAccountSource {
    if (!group.SmtpAddress || !group.DisplayName) {
        throw new Error('Both SMTP and DisplayName must be valid for building a Group Account');
    }

    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        group.SmtpAddress,
        memberOrOwnerUserIdentity
    );

    const characteristics = buildMailboxCharacteristics({
        supportsMail: true,
        supportsCalendar: true,
    });

    const m365mailboxType = M365MailboxType.Group;
    const m365mailbox = buildM365Mailbox(
        m365mailboxType,
        group.DisplayName,
        memberOrOwnerUserIdentity,
        group.SmtpAddress
    );

    const m365AccountSource = buildM365AccountSource({
        sourceId,
        displayName: group.DisplayName,
        characteristics,
        m365mailbox,
    });

    m365AccountSource.mailboxInfo.auxiliaryMailboxGuid = group.MailboxGuid;

    return {
        ...m365AccountSource,
        m365mailboxType,
        group,
    };
}

/**
 * Creates an M365SharedMailboxAccountSource
 * @param displayName
 * @param smtpAddress
 * @param hostingUserIdentity: userIdentity of the account source that hosts this archive
 * @returns M365SharedMailboxAccountSource
 */
export function buildM365SharedMailboxAccountSource(
    displayName: string,
    smtpAddress: string,
    hostingUserIdentity: string
): M365SharedMailboxAccountSource {
    const m365mailboxType = M365MailboxType.Shared;
    const m365account = buildM365AccountSourceWithoutSourceId(
        m365mailboxType,
        displayName,
        smtpAddress,
        hostingUserIdentity,
        {
            supportsMail: true,
        }
    );
    return {
        ...m365account,
        m365mailboxType,
    };
}

/**
 * Creates an M365ArchiveMailboxAccountSource
 * @param displayName
 * @param smtpAddress
 * @param hostingUserIdentity: userIdentity of the account source that hosts this archive
 * @returns M365ArchiveMailboxAccountSource
 */
export function buildM365ArchiveMailboxAccountSource(
    displayName: string,
    smtpAddress: string,
    hostingUserIdentity: string
): M365ArchiveMailboxAccountSource {
    const m365mailboxType = M365MailboxType.Archive;
    const m365account = buildM365AccountSourceWithoutSourceId(
        m365mailboxType,
        displayName,
        smtpAddress,
        hostingUserIdentity,
        {
            supportsMail: true,
        }
    );
    return {
        ...m365account,
        m365mailboxType,
    };
}

/**
 * Creates an M365PublicFolderMailboxAccountSource
 * @param displayName
 * @param smtpAddress
 * @param hostingUserIdentity: userIdentity of the account source that hosts this archive
 * @returns M365PublicFolderMailboxAccountSource
 */
export function buildM365PublicFolderMailboxAccountSource(
    displayName: string,
    smtpAddress: string,
    hostingUserIdentity: string
): M365PublicFolderMailboxAccountSource {
    const m365mailboxType = M365MailboxType.PublicFolder;
    const m365account = buildM365AccountSourceWithoutSourceId(
        m365mailboxType,
        displayName,
        smtpAddress,
        hostingUserIdentity,
        {
            supportsMail: true,
            supportsCalendar: true,
        }
    );
    return {
        ...m365account,
        m365mailboxType,
    };
}

/**
 * Creates an M365M365TeamsMailboxAccountSource
 * @param displayName
 * @param smtpAddress
 * @param hostingUserIdentity: userIdentity of the account source that hosts this archive
 * @returns M365TeamsMailboxAccountSource
 */
export function buildM365TeamsMailboxAccountSource(
    displayName: string,
    smtpAddress: string,
    hostingUserIdentity: string
): M365TeamsMailboxAccountSource {
    const m365mailboxType = M365MailboxType.Teams;
    const m365account = buildM365AccountSourceWithoutSourceId(
        m365mailboxType,
        displayName,
        smtpAddress,
        hostingUserIdentity,
        {
            supportsCalendar: true,
            supportsContacts: true,
        }
    );
    return {
        ...m365account,
        m365mailboxType,
    };
}

export function buildM365ConnectedMailboxAccountSourceFromHostSourceInformation(
    sourceId: string,
    hostAccount: HostSourceInformation
): M365ConnectedMailboxAccountSource {
    const characteristics = buildMailboxCharacteristicsFromHostSourceInformation(hostAccount);

    const m365mailbox = buildM365MailboxFromHostSourceInformation(
        M365MailboxType.User,
        hostAccount
    );

    const sourceType =
        hostAccount.authProviderType === SourceAuthType.Microsoft365
            ? AccountSourceType.Office365
            : AccountSourceType.OutlookDotCom;

    const externalData = hostAccount.externalData;

    const m365AccountSource = buildM365AccountSource({
        sourceId,
        displayName: hostAccount.displayName,
        characteristics,
        m365mailbox,
        sourceType,
        externalData,
    });

    const accountState = hostAccount.accountState ?? {
        connectedAccountState: 0 /** OWAConnectedAccountState.Valid */,
    };

    return {
        ...m365AccountSource,
        accountState,
        m365mailboxType: M365MailboxType.Connected,
    };
}
